import { z } from 'zod';
import { normalizedUuidSchema, UUID_PATTERN, uuidSchema } from './common';
import { designAssetViewSchema, MAX_RESOLVE_DESIGN_ASSET_IDS } from './design-api';
import { guestKeySchema } from './guest-contact';
import {
  ALLOWED_IMAGE_CONTENT_TYPES,
  type AllowedImageContentType,
  IMAGE_CONTENT_TYPE_EXTENSIONS,
} from './image-asset';

// ---------------------------------------------------------------------------
// 고객 이미지 업로드 계약(결정 D5·D6) — apps/api/src/modules/designs 의 designs.assets.presignUpload·confirmUpload 가 쓴다.
// 면 사진(storage.ts)과 같은 흐름이지만 사용자 스코프라 productId 가 없고, Redis 세션 대신 design_assets 의
// PENDING 행(ready_at IS NULL)이 세션이다. 임시 키는 행 id 에서 결정론적으로 나온다.
// 표시 정보(designAssetViewSchema)·URL 갱신 입력은 design-api.ts. 폰트 업로드는 font.ts.
// ---------------------------------------------------------------------------

/** 최종 키 `users/{user_id}/uploads/{id}.{ext}` 의 세그먼트 — design_assets 의 chk_design_assets_file_key_prefix 와 같다. */
export const DESIGN_ASSET_SEGMENT = 'uploads';
/** 임시 키 접두사. IAM TemporaryUploads(`tmp/*`)·수명주기 `tmp/`(1일) 아래에 든다(ADR-0008). */
export const DESIGN_ASSET_TEMP_PREFIX = 'tmp/uploads/';

const IMAGE_EXTENSION_ALTERNATION = Object.values(IMAGE_CONTENT_TYPE_EXTENSIONS).join('|');

/**
 * 최종 키 형식 전체 — `users/{uuid}/uploads/{uuid}.{jpg|png|webp}`. 그룹 1 = user_id, 그룹 2 = 에셋 id.
 * catalog ASSET_KEY_PATTERN 과 같은 이유로 세그먼트 전부를 고정해 `..` 가 끼어들 자리를 없앤다.
 */
export const DESIGN_ASSET_KEY_PATTERN = new RegExp(
  `^users/(${UUID_PATTERN})/${DESIGN_ASSET_SEGMENT}/(${UUID_PATTERN})\\.(${IMAGE_EXTENSION_ALTERNATION})$`,
);
/** 임시 키 형식 — `tmp/uploads/{uuid}.{jpg|png|webp}`. 그룹 1 = 에셋 id. */
export const DESIGN_ASSET_TEMPORARY_KEY_PATTERN = new RegExp(
  `^${DESIGN_ASSET_TEMP_PREFIX}(${UUID_PATTERN})\\.(${IMAGE_EXTENSION_ALTERNATION})$`,
);

/** 키 생성(api designs)과 키 형식(위 패턴)이 같은 표를 보도록 조립도 여기서 한다. */
export const designAssetFileKeyFor = (input: {
  readonly userId: string;
  readonly assetId: string;
  readonly contentType: AllowedImageContentType;
}): string =>
  `users/${input.userId}/${DESIGN_ASSET_SEGMENT}/${input.assetId}.${IMAGE_CONTENT_TYPE_EXTENSIONS[input.contentType]}`;
/**
 * 비회원 이미지 최종 키(비회원 견적 설계 G6) — `guests/uploads/{uuid}.{jpg|png|webp}`. 그룹 1 = 에셋 id.
 * 비회원은 user_id 가 없어 사용자 세그먼트가 없다. 회원 키 패턴(DESIGN_ASSET_KEY_PATTERN)과 서로를 받지 않는다 —
 * DB CHECK(`guests/uploads/%`)·worker 삭제 방어선·IAM `guests/*` 가 이 형식을 함께 쓴다. 임시 키는 회원과 같다(`tmp/uploads/`).
 */
export const GUEST_DESIGN_ASSET_KEY_PATTERN = new RegExp(
  `^guests/${DESIGN_ASSET_SEGMENT}/(${UUID_PATTERN})\\.(${IMAGE_EXTENSION_ALTERNATION})$`,
);
/** 비회원 이미지 키 조립 — api 비회원 업로드는 이 함수로만 키를 만든다. */
export const guestDesignAssetFileKeyFor = (input: {
  readonly assetId: string;
  readonly contentType: AllowedImageContentType;
}): string =>
  `guests/${DESIGN_ASSET_SEGMENT}/${input.assetId}.${IMAGE_CONTENT_TYPE_EXTENSIONS[input.contentType]}`;
export const designAssetTemporaryKeyFor = (input: {
  readonly assetId: string;
  readonly contentType: AllowedImageContentType;
}): string =>
  `${DESIGN_ASSET_TEMP_PREFIX}${input.assetId}.${IMAGE_CONTENT_TYPE_EXTENSIONS[input.contentType]}`;

/** 썸네일이 쓰는 이미지 형식 — design_thumbnails.file_key 의 확장자(ERD §2-4a). */
export const DESIGN_THUMBNAIL_EXTENSIONS = ['png', 'webp'] as const;
export type DesignThumbnailExtension = (typeof DESIGN_THUMBNAIL_EXTENSIONS)[number];

/**
 * 썸네일 키 형식 전체 — `users/{uuid}/designs/{uuid}/{uuid}.{png|webp}`(ERD §2-4a). 그룹 1 = user_id, 그룹 2 = 디자인 id.
 * DB CHECK 는 `users/%/designs/%` 접두사만 보므로, 생성기(Phase 8)와 worker 정리(키 검증 뒤 S3 삭제)가 이 형식을 함께 쓴다.
 */
export const DESIGN_THUMBNAIL_KEY_PATTERN = new RegExp(
  `^users/(${UUID_PATTERN})/designs/(${UUID_PATTERN})/${UUID_PATTERN}\\.(${DESIGN_THUMBNAIL_EXTENSIONS.join('|')})$`,
);
/** 썸네일 키 조립 — 생성기는 이 함수로만 키를 만든다(worker 가 같은 패턴으로 검증한다). */
export const designThumbnailKeyFor = (input: {
  readonly userId: string;
  readonly designId: string;
  readonly fileId: string;
  readonly extension: DesignThumbnailExtension;
}): string => `users/${input.userId}/designs/${input.designId}/${input.fileId}.${input.extension}`;

/** presign 뒤 confirm 할 수 있는 창(design_assets.created_at 기준). 지나면 410. */
export const DESIGN_ASSET_CONFIRM_EXPIRES_IN_SECONDS = 3600;

/**
 * 업로드 이미지 보관 기간(정책 §5) — DB 삭제 가드(guard_design_asset_delete)의 INTERVAL '7 days'·'30 days' 와 같은 값이다.
 * 한 번도 참조되지 않은 READY 는 ready_at 부터, 참조 이력이 있으면 마지막 해제(max(removed_at))부터 센다.
 * worker 정리 잡은 기간이 지난 이미지를 지우고, api 저장은 기간이 지난 이미지를 새로 참조하지 못하게 거른다.
 */
export const UNREFERENCED_DESIGN_ASSET_RETENTION_DAYS = 7;
export const RELEASED_DESIGN_ASSET_RETENTION_DAYS = 30;

/** 사용자당 최근 1시간 presign 상한. 초과는 429. */
export const MAX_DESIGN_ASSET_PRESIGNS_PER_HOUR = 100;

/** 사용자 스코프라 productId·purpose 가 없다 — 관리자용 presignUploadInputSchema 유니온에 끼우지 않는다. */
export const presignDesignAssetUploadInputSchema = z.strictObject({
  contentType: z.enum(ALLOWED_IMAGE_CONTENT_TYPES),
});
export type PresignDesignAssetUploadInput = z.infer<typeof presignDesignAssetUploadInputSchema>;

/** 응답은 non-strict — 라우터 .output() 이 StoragePort.presignPut 의 여분 key 를 떨어뜨린다. */
export const presignDesignAssetUploadResultSchema = z.object({
  /** design_assets.id — confirmUpload 와 이미지 노드 assetId 가 이 값을 쓴다. */
  assetId: uuidSchema,
  uploadUrl: z.url(),
  temporaryKey: z.string().regex(DESIGN_ASSET_TEMPORARY_KEY_PATTERN),
  expiresInSeconds: z.number().int().positive(),
  confirmExpiresInSeconds: z.number().int().positive(),
});
export type PresignDesignAssetUploadResult = z.infer<typeof presignDesignAssetUploadResultSchema>;

/** 브라우저 크기를 받지 않는다 — 서버 판독값(width_px·height_px)이 원천이다. */
export const confirmDesignAssetUploadInputSchema = z.strictObject({
  assetId: normalizedUuidSchema,
});
export type ConfirmDesignAssetUploadInput = z.infer<typeof confirmDesignAssetUploadInputSchema>;
export const confirmDesignAssetUploadResultSchema = designAssetViewSchema;
export type ConfirmDesignAssetUploadResult = z.infer<typeof confirmDesignAssetUploadResultSchema>;

// ---------------------------------------------------------------------------
// 비회원 이미지 업로드 입력(비회원 견적 설계 G6) — designs.guestAssets.presignUpload·confirmUpload·resolveUrls(공개).
// 소유는 비회원 키(guestKey)로 확인한다. 출력은 회원 API 와 같은 스키마(presign 결과·designAssetViewSchema·resolve 결과)다.
// ---------------------------------------------------------------------------

export const guestDesignAssetPresignInputSchema = z.strictObject({
  guestKey: guestKeySchema,
  contentType: z.enum(ALLOWED_IMAGE_CONTENT_TYPES),
});
export type GuestDesignAssetPresignInput = z.infer<typeof guestDesignAssetPresignInputSchema>;
export const guestDesignAssetConfirmInputSchema = z.strictObject({
  guestKey: guestKeySchema,
  assetId: normalizedUuidSchema,
});
export type GuestDesignAssetConfirmInput = z.infer<typeof guestDesignAssetConfirmInputSchema>;
/** 표시 URL 갱신 — 다른 비회원 키의 이미지·PENDING·없음이 하나라도 섞이면 회원 API 처럼 전체 404. */
export const guestDesignAssetResolveInputSchema = z.strictObject({
  guestKey: guestKeySchema,
  assetIds: z.array(normalizedUuidSchema).min(1).max(MAX_RESOLVE_DESIGN_ASSET_IDS),
});
export type GuestDesignAssetResolveInput = z.infer<typeof guestDesignAssetResolveInputSchema>;

// ---------------------------------------------------------------------------
// 업로드 거부 문구(결정 E1). 만료(410)는 tRPC 에 GONE 이 없어 BAD_REQUEST 로 나가므로 판독 실패(400)와
// 코드로는 갈리지 않는다 — web 은 message 를 이 상수와 대조해 안내를 가른다. 판독 실패 400 문구는 인스펙터 것 그대로다.
// ---------------------------------------------------------------------------

/** 404 — 타인 소유·없음, resolveUrls 의 PENDING 포함을 같은 문구로 숨긴다. */
export const DESIGN_ASSET_NOT_FOUND_MESSAGE = '이미지를 찾을 수 없습니다.';
/** 410 — presign 뒤 DESIGN_ASSET_CONFIRM_EXPIRES_IN_SECONDS 가 지났다. 같은 행으로는 다시 확정할 수 없다. */
export const DESIGN_ASSET_UPLOAD_EXPIRED_MESSAGE =
  '임시 업로드가 만료되었습니다. 다시 업로드해 주세요.';
/** 429 — 최근 1시간 presign 이 MAX_DESIGN_ASSET_PRESIGNS_PER_HOUR 에 이르렀다. */
export const DESIGN_ASSET_PRESIGN_RATE_LIMIT_MESSAGE =
  '이미지 업로드 요청이 너무 잦습니다. 잠시 후 다시 시도해 주세요.';
/** 409 — 복사 뒤 최종 객체가 없거나 판독한 원본과 다르다(같은 최종 키에 다른 파일이 먼저 자리한 경우). */
export const DESIGN_ASSET_FINALIZED_MISMATCH_MESSAGE =
  '저장된 이미지가 판독한 파일과 다릅니다. 다시 업로드해 주세요.';
/** 409 — READY 전이 CAS 가 0건이고 먼저 전이한 요청의 sha256 이 이번 판독과 다르다. */
export const DESIGN_ASSET_CONFIRM_CONFLICT_MESSAGE =
  '확정 중 이미지가 변경되었습니다. 다시 업로드해 주세요.';
/** 503 — presign·판독·복사·재대조 중 스토리지 호출이 실패했다. 원인은 서버 쪽 cause 로만 남긴다. */
export const DESIGN_ASSET_STORAGE_UNAVAILABLE_MESSAGE =
  '이미지 업로드 처리에 실패했습니다. 다시 시도해 주세요.';
